import { useMemo, useState } from "react";
import { stars } from "@/mocks/stars";
import StarCard from "@/components/feature/StarCard";
import PageHeader from "@/components/feature/PageHeader";
import EmptyState from "@/components/base/EmptyState";
import { useSEO } from "@/hooks/useSEO";

const genderOptions = ["全部", "男", "女"];
const eraOptions = ["全部", "00后", "95后", "90后", "80后", "70后"];
const activeOptions = ["全部", "一线主演", "实力派", "资深戏骨", "上升期", "新人"];
const PAGE_SIZE = 8;

function FilterRow({
  label,
  options,
  value,
  onChange,
}: {
  label: string;
  options: string[];
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <div className="flex flex-col gap-2 border-b border-background-200 py-3 first:pt-0 last:border-b-0 last:pb-0 sm:flex-row sm:items-center">
      <span className="w-16 shrink-0 text-sm font-medium text-foreground-500">{label}</span>
      <div className="flex flex-wrap gap-1.5">
        {options.map((opt) => (
          <button
            key={opt}
            type="button"
            onClick={() => onChange(opt)}
            className={`whitespace-nowrap rounded-full px-3 py-1 text-sm transition-colors ${
              value === opt
                ? "bg-primary-500 text-background-50"
                : "bg-background-100 text-foreground-600 hover:bg-background-200"
            }`}
          >
            {opt}
          </button>
        ))}
      </div>
    </div>
  );
}

export default function Stars() {
  useSEO({
    title: "明星资讯 - 朝桐光品牌",
    description:
      "朝桐光签约演员与主创团队名录，收录演员形象、代表作品与基础资料，支持按性别、年代、活跃状态筛选查阅。",
    keywords: "朝桐光明星,朝桐光演员,明星资讯,演员名录",
  });

  const [gender, setGender] = useState("全部");
  const [era, setEra] = useState("全部");
  const [active, setActive] = useState("全部");
  const [visible, setVisible] = useState(PAGE_SIZE);

  const filtered = useMemo(
    () =>
      stars.filter((s) => {
        if (gender !== "全部" && s.gender !== gender) return false;
        if (era !== "全部" && s.era !== era) return false;
        if (active !== "全部" && s.active !== active) return false;
        return true;
      }),
    [gender, era, active],
  );

  const pageItems = filtered.slice(0, visible);

  const resetAll = () => {
    setGender("全部");
    setEra("全部");
    setActive("全部");
    setVisible(PAGE_SIZE);
  };

  const change = (setter: (v: string) => void) => (v: string) => {
    setter(v);
    setVisible(PAGE_SIZE);
  };

  return (
    <div>
      <PageHeader
        title="明星资讯"
        desc="查看朝桐光关联明星的详细资料与代表作品。可按性别、年代与活跃状态筛选，快速找到你关注的演员。"
        breadcrumb={[{ label: "首页", to: "/" }, { label: "明星资讯" }]}
        aside={
          <div className="flex items-center gap-6 rounded-lg border border-primary-900 bg-primary-900/40 px-5 py-3">
            <div>
              <div className="font-heading text-2xl font-black text-accent-400">{stars.length}</div>
              <div className="text-xs text-background-300">全部明星</div>
            </div>
            <div className="h-8 w-px bg-primary-800" />
            <div>
              <div className="font-heading text-2xl font-black text-background-50">{filtered.length}</div>
              <div className="text-xs text-background-300">筛选结果</div>
            </div>
          </div>
        }
      />

      <div className="mx-auto w-full max-w-page px-4 py-10 md:px-6 md:py-14">
        <div className="rounded-lg border border-background-200 bg-background-100/60 p-4 md:p-5">
          <FilterRow label="性别" options={genderOptions} value={gender} onChange={change(setGender)} />
          <FilterRow label="年代" options={eraOptions} value={era} onChange={change(setEra)} />
          <FilterRow label="状态" options={activeOptions} value={active} onChange={change(setActive)} />
          <div className="flex justify-end pt-3">
            <button
              type="button"
              onClick={resetAll}
              className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 px-3 py-1.5 text-sm text-foreground-600 transition-colors hover:border-primary-400 hover:text-primary-600"
            >
              <i className="ri-refresh-line"></i>
              重置筛选
            </button>
          </div>
        </div>

        <div className="mt-8">
          {pageItems.length === 0 ? (
            <EmptyState
              title="没有找到符合条件的明星"
              desc="试试更换筛选条件，或点击下方按钮重置全部筛选。"
              onReset={resetAll}
            />
          ) : (
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:gap-5 lg:grid-cols-4">
              {pageItems.map((star) => (
                <StarCard key={star.id} star={star} />
              ))}
            </div>
          )}
        </div>

        {pageItems.length > 0 && (
          <div className="mt-10 flex flex-col items-center gap-3">
            <p className="text-sm text-foreground-500">
              已显示 {pageItems.length} / {filtered.length} 位明星
            </p>
            {visible < filtered.length && (
              <button
                type="button"
                onClick={() => setVisible((v) => v + PAGE_SIZE)}
                className="inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
              >
                <i className="ri-add-line"></i>
                加载更多
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}